import { AppState, AppAction, DEFAULT_SETTINGS, PokemonTeam, MAX_TEAM_SIZE } from "../types/pokemon";

export const INITIAL_STATE: AppState = {
  screen: "home",
  teams: [
    { pokemon: [], name: "Joueur 1" },
    { pokemon: [], name: "Joueur 2" },
  ],
  currentPlayer: 1,
  draftPool: [],
  draftCards: [],
  rerollsRemaining: [DEFAULT_SETTINGS.maxRerolls, DEFAULT_SETTINGS.maxRerolls],
  settings: DEFAULT_SETTINGS,
  audio: { musicEnabled: false, sfxEnabled: true },
  loading: false,
  error: null,
  mode: "2p",
  aiDifficulty: "normal",
  savedTeams: [],
};

export function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case "SET_SCREEN":
      return { ...state, screen: action.screen };

    case "SET_DRAFT_CARDS":
      return { ...state, draftCards: action.cards, loading: false };

    case "SET_LOADING":
      return { ...state, loading: action.loading };

    case "SET_ERROR":
      return { ...state, error: action.error, loading: false };

    case "PICK_POKEMON": {
      const playerIdx = state.currentPlayer === 1 ? 0 : 1;
      const team = state.teams[playerIdx];
      if (team.pokemon.length >= MAX_TEAM_SIZE) return state;

      const allIds = [...state.teams[0].pokemon, ...state.teams[1].pokemon].map(p => p.id);
      if (allIds.includes(action.pokemon.id)) return state;

      const newTeams = [...state.teams] as [PokemonTeam, PokemonTeam];
      newTeams[playerIdx] = {
        ...team,
        pokemon: [...team.pokemon, action.pokemon],
      };

      const newPool = state.draftPool.filter(id => id !== action.pokemon.id);
      const nextPlayer: 1 | 2 = state.currentPlayer === 1 ? 2 : 1;

      const bothFull = newTeams[0].pokemon.length >= MAX_TEAM_SIZE
        && newTeams[1].pokemon.length >= MAX_TEAM_SIZE;

      return {
        ...state,
        teams: newTeams,
        currentPlayer: nextPlayer,
        draftPool: newPool,
        screen: bothFull ? "battle" : state.screen,
      };
    }

    case "REMOVE_POKEMON": {
      const idx = action.player === 1 ? 0 : 1;
      const newTeams = [...state.teams] as [PokemonTeam, PokemonTeam];
      newTeams[idx] = {
        ...state.teams[idx],
        pokemon: state.teams[idx].pokemon.filter(p => p.id !== action.pokemonId),
      };
      return {
        ...state,
        teams: newTeams,
        draftPool: [...state.draftPool, action.pokemonId],
      };
    }

    case "CLEAR_TEAM": {
      const idx = action.player === 1 ? 0 : 1;
      const clearedIds = state.teams[idx].pokemon.map(p => p.id);
      const newTeams = [...state.teams] as [PokemonTeam, PokemonTeam];
      newTeams[idx] = { ...state.teams[idx], pokemon: [] };
      return {
        ...state,
        teams: newTeams,
        draftPool: [...state.draftPool, ...clearedIds],
      };
    }

    case "RESET_DRAFT":
      return {
        ...state,
        teams: [
          { pokemon: [], name: "Joueur 1" },
          { pokemon: [], name: "Joueur 2" },
        ],
        currentPlayer: 1,
        draftPool: [],
        draftCards: [],
        rerollsRemaining: [state.settings.maxRerolls, state.settings.maxRerolls],
        screen: "home",
        mode: "2p",
      };

    case "USE_REROLL": {
      const ri = state.currentPlayer === 1 ? 0 : 1;
      if (state.rerollsRemaining[ri] <= 0) return state;
      const newRerolls = [...state.rerollsRemaining] as [number, number];
      newRerolls[ri]--;
      return { ...state, rerollsRemaining: newRerolls, draftCards: [] };
    }

    case "UPDATE_SETTINGS": {
      const newSettings = { ...state.settings, ...action.settings };
      return {
        ...state,
        settings: newSettings,
        rerollsRemaining: [newSettings.maxRerolls, newSettings.maxRerolls],
      };
    }

    case "SET_DRAFT_POOL":
      return { ...state, draftPool: action.pool };

    case "TOGGLE_MUSIC":
      return { ...state, audio: { ...state.audio, musicEnabled: !state.audio.musicEnabled } };

    case "TOGGLE_SFX":
      return { ...state, audio: { ...state.audio, sfxEnabled: !state.audio.sfxEnabled } };

    case "RESTORE_STATE":
      return {
        ...state,
        teams: action.teams,
        currentPlayer: action.currentPlayer,
      };

    case "SET_MODE":
      return {
        ...state,
        mode: action.mode,
        aiDifficulty: action.difficulty ?? state.aiDifficulty,
      };

    case "SAVE_TEAM":
      return { ...state, savedTeams: [...state.savedTeams, action.team] };

    case "DELETE_SAVED_TEAM":
      return { ...state, savedTeams: state.savedTeams.filter(t => t.id !== action.id) };

    default:
      return state;
  }
}
